(ns minorodata.components.connections.forms.generator
  (:require
   ["lucide-react" :refer [CheckCircle XCircle]]
   ["randomatic" :as randomatic]
   ["react" :as react]
   [clojure.core]
   [clojure.string :as str]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.components.connections.forms.conditionals :refer [test-conditions]]
   [minorodata.components.forms.api-connection-test :as forms.api-connection-test]
   [minorodata.components.forms.doc :as docs]
   [minorodata.components.forms.formatted-text :as forms.formatted-text]
   [minorodata.components.forms.inputs :as inputs]
   [minorodata.components.forms.metadata-select :as forms.metadata-select]
   [minorodata.components.forms.oauth :as forms.oauth]
   [minorodata.components.forms.recipes-select :as forms.recipes-select]
   [minorodata.components.forms.warehouse :as forms.warehouse]
   [minorodata.components.spinner.core :as spinner.core]
   [mold-forms.core :as form-component]
   [mold-forms.model :refer [->form-model]]
   [re-posh.core :as re-posh]
   [ui.alert :as alert]
   [ui.button :as button]
   [ui.datepicker]
   [ui.dialog :as dialog]
   [ui.fields]
   [ui.typography :as typography]
   [ui.utils :refer [clsx]]))


(defn process-checkbox-evt [value]
  value)


(defn process-date-evt
  "DatePicker returns the inst inside the first param"
  [value]
  value)


(defn process-raw-value [value]
  value)


(defn process-raw-value-evt [_evt value]
  value)

(defn process-select-evt [multiple? evt _]
  evt)


(defn convert-select-option [option]
  (cond
    (string? option) {:value option :text option :data-cy (str "select-option-" option)}
    (map? option)    {:value (str (:value option)) :text (:name option) :data-cy (:data-cy option)}
    :else            option))

(defn options-native? [options]
  (let [first-option (first options)]
    (and (vector? first-option)
         (= :> (first first-option)))))


(defn prepare-field
  "Cleans up block definition so only valid form field keys are left."
  [field]
  (let [value (:value field)]
    (cond-> (select-keys field [:label :name :value :type])

      (= :checkbox (:block-type field))
      (assoc :process process-checkbox-evt
             :value   (if (string? value)
                        (not (str/blank? value))
                        (boolean value)))

      (= :oauth (:block-type field))
      (assoc :process process-raw-value-evt)

      (= :oauth-v2 (:block-type field))
      (assoc :process process-raw-value-evt)

      (= :api-connection-test (:block-type field))
      (assoc :process process-raw-value-evt)

      (= :database-metadata-select (:block-type field))
      (assoc :process (partial process-select-evt (:multiple field)))

      (= :recipe-source-select (:block-type field))
      (assoc :process process-raw-value)

      (= :recipe-extract-select (:block-type field))
      (assoc :process process-raw-value)

      (= :recipe-group-select (:block-type field))
      (assoc :process process-raw-value)

      (= :select (:block-type field))
      (assoc :process process-raw-value)

      (= :date-picker (:block-type field))
      (assoc :process process-date-evt)

      (= :file-input (:block-type field))
      (assoc :process (partial identity))

      (= :generate-password (:block-type field))
      (assoc :process process-raw-value-evt
             :value   (if (or (nil? value)
                              (and (string? value)
                                   (str/blank? value)))
                        (randomatic "Aa0" 12)
                        value))

      (= :warehouse-destination (:block-type field))
      (assoc :process process-raw-value)

      (= :warehouse-destination-database (:block-type field))
      (assoc :process process-raw-value)

      (= :multiselect (:block-type field))
      (assoc :process identity)

      (= :downstream-config (:block-type field))
      (assoc :process process-checkbox-evt
             :value   (if (string? value)
                        (not (str/blank? value))
                        (boolean value))))))


(def enabled-block-types
  "Block types that are affecting forms and should have corresponding `:action` properties"
  #{:api-connection-test :checkbox :database-metadata-select :date-picker :downstream-config :file-input
    :form-value :generate-password :input :multiselect :oauth :oauth-v2 :recipe-extract-select
    :recipe-group-select :recipe-source-select :select :source-select :warehouse-destination :warehouse-destination-database :warehouse-status})



(def multi-field-blocks
  {:advanced-options [{:name       :advanced-options
                       :block-type :checkbox}
                      {:name       :start-date
                       :block-type :date-picker}
                      {:name       :end-date
                       :block-type :date-picker}
                      {:name       :batch-size
                       :block-type :select}]})


(defn prepare-fields
  "Prepare the fields for blocks which generate multiple inputs."
  [block]
  (map prepare-field
       (get multi-field-blocks (:block-type block))))


(defn blocks->fields
  "Converts form generator definition into a vector of fields for form component."
  [blocks]
  (let [inputs (filter
                #(contains?
                  enabled-block-types
                  (:block-type %))
                blocks)
        multi-inputs (filter (comp (set (keys multi-field-blocks))
                                   :block-type)
                             blocks)]
    (concat (map prepare-field inputs)
            (mapcat prepare-fields multi-inputs))))


(defn typography-reference-block [{:keys [path fields component]
                                   :or   {component "p"}}]
  [component {}
   (get-in fields path)])


(defn typography-block [{:keys [content component]
                         :or   {component "p"}}]
  [component {}
   content])


(defn content-block [content]
  (if (string? content)
    [:p content]
    content))


(defn generate-data-cy [block-type input-name]
  (str (if (some? block-type)
         (name block-type)
         "input")
       "-"
       (if (some? input-name)
         (name input-name)
         "cy")))

(defn filestore-status []
  (let [saving?       @(re-posh/subscribe [:mold-http/loading? :connections.api/save-warehouse])
        status        @(re-posh/subscribe [:filestore-status/pull])
        loading?      @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-filestore-status])
        valid?        (= "ok" (:status (:filestore-status/store-status status)))]
    [:div {:class "flex gap-4 items-center"}
     (cond
       saving?
       [:<>
        [spinner.core/spinner-lucide {:size 20}]
        [typography/p "Saving Filestore"]]

       loading?
       [:<>
        [spinner.core/spinner-lucide {:size 20}]
        [typography/p "Fetching Filestore status"]]

       valid?
       [:<>
        [:> CheckCircle {:size 20 :class "text-kleene-text-success"}]
        [typography/p "Valid Filestore configuration"]]

       (not valid?)
       [:<>
        [:> XCircle {:size 20 :class "text-kleene-text-error"}]
        [typography/p (:error (:filestore-status/store-status status))]])]))

(defn fill-form [form-id private-key passphrase]
  (dispatch-sync [:app.events/update-form-field form-id :private-key private-key])
  (dispatch-sync [:app.events/update-form-field form-id :passphrase (or passphrase "")]))

(defn generate-keypair' [{:keys [form-id]}]
  (let [[{:keys [private-key public-key passphrase]} set-keys] (react/useState nil)]
    [:div
     [dialog/dialog-root {:on-open-change #(when-not % (set-keys nil))}
      [dialog/dialog-trigger {:as-child true}
       [:> button/button-fr
        {:variant :secondary
         :size    :small}
        "Generate key pair"]]
      [dialog/dialog-content
       [dialog/dialog-title
        "Generate key pair"]
       [dialog/dialog-description "Use form below to generate key pair."]

       [:div {:class "mt-4"}
        (if private-key
          [:div {:class "flex flex-col gap-2"}
           [alert/alert {:variant "warn"}
            "Please copy these secrets and save it somewhere safe. For security reasons, we can't show the secret to you again."]

           [ui.fields/input {:value     private-key
                             :on-change (fn [])
                             :label     "Private key"
                             :data-cy   "private-key"
                             :textarea  true
                             :rows      5}]
           [ui.fields/input {:value     public-key
                             :on-change (fn [])
                             :label     "Public key"
                             :data-cy   "public-key"
                             :textarea  true
                             :rows      5}]
           [ui.fields/input {:value     (or passphrase "")
                             :on-change (fn [])
                             :label     "Passphrase"
                             :data-cy   "passphrase"
                             :type      :password}]
           [:div {:class "flex justify-end"}

            [dialog/dialog-close
             [:> button/button-fr {:on-click (fn []
                                               (fill-form form-id private-key (or passphrase "")))}
              "Fill the form with keys"]]]]

          [form-component/form
           (->form-model
            {:id        :generate-key-pair-form
             :on-submit (fn [data]
                          (let [fields (form-component/model->fields data)]
                            (dispatch [:connections.api/generate-keypair fields {:on-success set-keys}])))
             :clean-up  #(dispatch [:form-model/delete [:form-model/id :generate-keypair-form]])
             :fields    [{:name :passphrase :value ""}]})
           (fn [fields errors on-submit]
             [:<>
              [ui.fields/input {:field     (get fields :passphrase)
                                :errors    errors
                                :id        :passphrase
                                :name      :passphrase
                                :type      :password
                                :label     "Passphrase (optional)"
                                :data-cy   "passphrase"
                                :input-props {:auto-focus true
                                              :auto-complete "off"}}]
              [dialog/dialog-actions
               [button/button {:on-click on-submit}
                "Generate"]]])])]]]]))

(defn generate-keypair [props]
  [:f> generate-keypair' props])

(defn warehouse-status [{:keys [role-id user-id]}]
  (let [_ (dispatch [:connections.api/fetch-warehouse-status {:user-id user-id
                                                              :role-id role-id
                                                              :refresh-data? false}])]
    (fn []
      (let [status        @(re-posh/subscribe [:warehouse-status/pull])
            loading?      @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-warehouse-status])
            saving?       @(re-posh/subscribe [:mold-http/loading? :connections.api/save-warehouse])
            valid?        (= "ok" (:status (:warehouse-status/sink-status status)))]
        [:div {:class "flex gap-4 items-center"}
         (cond
           saving?
           [:<>
            [spinner.core/spinner-lucide {:size 20}]
            [typography/p "Saving Warehouse"]]

           loading?
           [:<>
            [spinner.core/spinner-lucide {:size 20}]
            [typography/p "Fetching Warehouse status"]]

           valid?
           [:<>
            [:> CheckCircle {:size 20 :class "text-kleene-text-success"}]
            [typography/p "Valid Warehouse configuration"]]

           (not valid?)
           [:<>
            [:> XCircle {:size 20 :class "text-kleene-text-error"}]
            [typography/p
             (:error (:warehouse-status/sink-status status))]])]))))

(defn render-input-block
  "Renders form block based on `:block-type` field.
   :doc - provides a docstring to be rendered as a question mark to the right of the input.
   Docstring will be displayed in a tooltip. Can be a string or a hiccup vector:
   `[:span \"Link to external docs: \" [:a {:href \"https://example.com\"} \"link\"]]`"
  [block opts]
  (let [{:keys [label name type content required options multiple multiline
                conditions conditions-fn placeholder disabled native
                path component min-date max-date doc format args
                class controlled]
         :or   {type      :input
                multiline false
                multiple  false
                disabled  false
                component "p"
                path      []
                native    (options-native? options)
                conditions-fn (constantly true)}} block
        {:keys [form-id fields errors kleene-globals params]} opts

        block-type (:block-type block)
        data-cy    (generate-data-cy block-type name)
        full-data  (assoc fields :kleene-globals kleene-globals)]
    [:f> inputs/block-wrapper
     {:doc
      (when doc
        [docs/tooltip {:doc       doc
                       :class     (cond
                                    (= :checkbox block-type) "mt-0"
                                    (= :subtitle block-type) "mt-3")
                       :align-top (or (nil? block-type)
                                      (= :database-metadata-select block-type)
                                      (= :file-input block-type)
                                      (= :typography-block block-type)
                                      (= :typography-reference-block block-type)
                                      (= :typography-formatted-block block-type))}])
      :input
      (when (and (test-conditions conditions full-data)
                 (conditions-fn full-data))
        (case block-type
          :input
          ^{:key block}
          [ui.fields/input
           (merge
            {:field         (get fields name)
             :errors        errors
             :id            name
             :name          name
             :type          type
             :required      required
             :disabled      disabled
             :textarea      multiline
             :placeholder   placeholder
             :data-cy       data-cy
             :label         label
             :class         (clsx "" class)
             :controlled    (or controlled false)

             ;; Hacks to disable autofill by password managers
             :input-props   {:auto-complete "off"
                             :data-lpignore "true"
                             :role          "presentation"}})]

          :select
          ^{:key (str block)}
          [ui.fields/select
           {:field         (get fields name)
            :multiple      multiple
            :errors        errors
            :id            name
            :name          name
            :type          type
            :required      required
            :disabled      disabled
            :native        false
            :options       (mapv convert-select-option options)
            :label         label
            :data-cy       data-cy
            :class         class}]

          :checkbox
          ^{:key block}
          [ui.fields/checkbox
           {:field      (get fields name)
            :errors     errors
            :id         name
            :type       type
            :full-width true
            :required   required
            :disabled   disabled
            :data-cy    data-cy
            :label      label}]

          :date-picker
          ^{:key block}
          [ui.datepicker/date-picker
           {:field    (get fields name)
            :min-date min-date
            :max-date max-date
            :label    label}]

          :database-metadata-select
          ^{:key (str block
                      (map #(get-in fields [% :value])
                           (:upstream-deps block)))}
          [forms.metadata-select/database-metadata-select
           {:block   block
            :fields  fields
            :errors  errors}]

          :source-select
          ^{:key block}
          [inputs/source-select
           {:field   (get fields name)
            :block   block
            :fields  fields
            :errors  errors}]

          :advanced-options
          ^{:key block}
          [inputs/advanced-options
           {:block    block
            :fields   fields
            :multiple multiple
            :errors   errors
            :type     type
            :native   native
            :data-cy  data-cy}]

          :submit
          ^{:key block}
          [:div {:class "flex w-full justify-end"}
           [ui.button/button
            {:type       "submit"
             :variant    :primary
             :data-cy    data-cy
             :disabled   disabled
             :class      class}
            content]]


          :form-value
          nil


          :generate-password
          nil


          :subtitle
          ^{:key block}
          [:div {:class "mt-4 h-6"}
           [typography/h4 content]]


          :oauth
          ^{:key (str block (get-in fields [name :value]))}
          [forms.oauth/sign-in
           {:configuration block
            :field         (get fields name)
            :fields        fields
            :errors        errors
            :disabled      disabled
            :id            name}]


          ;; Same oauth component, just with declarative only configuration, no
          ;; functions as parameters
          :oauth-v2
          ^{:key (str block (get-in fields [name :value]))}
          [forms.oauth/sign-in-v2
           {:configuration  block
            :field          (get fields name)
            :errors         errors
            :disabled       disabled
            :id             name
            :fields         fields
            :kleene-globals kleene-globals}]


          :api-connection-test
          ^{:key block}
          [forms.api-connection-test/check-conn
           {:block        block
            :fields       fields
            :source       params}]


          :file-input
          ^{:key name}
          [inputs/file-input
           {:field    (get fields name)
            :id       name
            :required required
            :label    label}]


          :typography-block
          ^{:key block}
          [typography-block {:content content :component component}]


          :typography-reference-block
          ^{:key block}
          [typography-reference-block {:fields fields :component component :path path}]


          :typography-formatted-block
          ^{:key block}
          [forms.formatted-text/typography-formatted-block
           {:fields         fields
            :component      component
            :format         format
            :args           args
            :kleene-globals kleene-globals}]

          :warehouse-status
          ^{:key block}
          [:div {:class "mt-2"}
           [warehouse-status]
           [filestore-status]]

          :multiselect
          ^{:key block}
          [:f> forms.metadata-select/multiselect
           {:disabled disabled
            :field    (get fields name)
            :label    label
            :options  options}]

          :recipe-source-select
          ^{:key (str block
                      (get-in fields [name :value])
                      (map #(get-in fields [% :value])
                           (:upstream-deps block)))}
          [forms.recipes-select/source-select
           {:block   block
            :data-cy data-cy
            :fields  fields
            :errors  errors}]

          :recipe-extract-select
          ^{:key (str block
                      (get-in fields [name :value])
                      (map #(get-in fields [% :value])
                           (:upstream-deps block)))}
          [forms.recipes-select/extract-select
           {:block   block
            :data-cy data-cy
            :fields  fields
            :errors  errors}]

          :recipe-group-select
          ^{:key (str block
                      (get-in fields [name :value])
                      (map #(get-in fields [% :value])
                           (:upstream-deps block)))}
          [forms.recipes-select/group-select
           {:block   block
            :data-cy data-cy
            :fields  fields
            :errors  errors}]

          :warehouse-destination
          ^{:key (str block)}
          [forms.warehouse/destination-select
           {:block   block
            :field   (get fields name)
            :errors  errors}]

          :warehouse-destination-database
          ^{:key (str block)}
          [forms.warehouse/database-select
           {:block   block
            :field   (get fields name)
            :errors  errors}]

          ;; Replicated `:checkbox` block as we might need different implementation in the future
          :downstream-config
          ^{:key block}
          [ui.fields/checkbox
           {:field      (get fields name)
            :errors     errors
            :id         name
            :type       type
            :full-width true
            :required   required
            :disabled   disabled
            :data-cy    data-cy
            :label      "Run downstream on finish"}]

          :generate-keypair
          ^{:key block}
          [generate-keypair
           {:form-id form-id}]

          :alert
          ^{:key block}
          [alert/alert {:title (:title block) :variant (:variant block)}
           content]

          ;; Escape hatch — block provides its own renderer fn via `:render`.
          ;; Does NOT participate in form state (filtered out of fields).
          ;; Receives a context map: {:block :fields :errors :form-id
          ;; :params :kleene-globals} — use it to read state or dispatch updates.
          :custom
          ^{:key block}
          [(:render block) {:block          block
                            :fields         fields
                            :errors         errors
                            :form-id        form-id
                            :params         params
                            :kleene-globals kleene-globals}]

          ^{:key block}
          [content-block content]))}]))

(defn merge-initial-values
  "If a map of initial values is provided, updates form field definitions
  with new initial values."
  [fields values]
  (if (some? values)
    (reduce (fn [updated-fields field]
              (let [key (:name field)
                    val (get values key)]
                (conj updated-fields
                      (if (contains? values key)
                        (assoc field :value val)
                        field))))
            []
            fields)
    fields))

;; Hack for non-jumper connectors
(defn maybe-add-api-downstream-config [api-data data]
  (if-let [ds-config (or (:downstream-config data) (:extract/downstream-config data))]
    (assoc api-data
           :downstream-config (cond
                                (string? ds-config) ds-config
                                (true? ds-config) "full"
                                :else nil))
    api-data))

(defn maybe-add-input-downstream-config [data initial-data]
  (if-let [ds-config (:extract/downstream-config initial-data)]
    (assoc data
           :downstream-config (= ds-config "full"))
    data))
;;

(defn generate
  "Takes form definition and optional params, then generates a form that will fit into sidebar."
  [form-definition {:keys [on-close initial-values submit kleene-globals params]}]
  (let [{:keys [id format-for-api format-for-inputs validate blocks]
         :or   {format-for-api identity format-for-inputs identity}} form-definition
        clean-submit      (fn [form-data]
                            (let [data     (form-component/model->fields form-data)
                                  api-data (maybe-add-api-downstream-config (format-for-api data) data)]
                              (submit api-data)
                              (on-close)))
        clear-form        #(dispatch [:form-model/delete [:form-model/id id]])
        field-definitions (blocks->fields blocks)
        prefill-values    (maybe-add-input-downstream-config (format-for-inputs initial-values) initial-values)
        fields            (merge-initial-values field-definitions prefill-values)]
    [form-component/form
     (->form-model
      {:id        id
       :on-submit clean-submit
       :clean-up  clear-form
       :validate  validate
       :fields    fields})
     (fn [fields errors on-submit]
       (into
        ;; :key is needed here so that conditional fields keep up with
        ;; form state and are re-rendered properly
        [:form {:class          "flex flex-col space-y-4 mb-4"
                :on-submit      on-submit
                :key            fields
                :auto-complete  "off"
                :data-form-type "other"}]
        (map #(render-input-block % {:form-id id
                                     :fields fields
                                     :errors errors
                                     :on-submit on-submit
                                     :model prefill-values
                                     :kleene-globals kleene-globals
                                     :params params})
             blocks)))]))
